import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notify } from '../stores/notificationStore';
import { invoicesAPI, quotesAPI } from '../utils/api';
import { productsAPI } from '../utils/productsAPI';
import { calculateTotalsWithVat, isVatExempt } from '../utils/vatCalculations';
import { resolveTierPrice } from '../utils/priceTierUtils';

/**
 * Convertit une date ISO ou Date en format yyyy-MM-dd pour les inputs HTML
 */
export function formatDateForInput(dateString: string | Date): string {
  if (!dateString) return '';

  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

  if (isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Convertit une date yyyy-MM-dd en format ISO UTC
 */
export function convertToISO(dateStr: string): string {
  if (!dateStr) return new Date().toISOString();
  // Si déjà en ISO, retourner tel quel
  if (dateStr.includes('T')) return dateStr;
  // Convertir yyyy-MM-dd en ISO avec UTC midnight
  return new Date(dateStr + 'T00:00:00.000Z').toISOString();
}

// ✅ V2: Interface alignée avec QuoteItem/InvoiceItem de Prisma V2
export interface DocumentEditItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxPercent: number;  // ✅ CORRIGÉ: V2 utilise taxPercent au lieu de vat_rate
  total: number;  // Total HT (quantity * unitPrice)
  position?: number;  // ✅ AJOUTÉ: V2 utilise position
  productId?: string;  // ✅ AJOUTÉ: Référence au produit (optionnel)
  taxRateId?: string;  // ✅ AJOUTÉ: Référence au taux de TVA (optionnel)
  unit?: string;  // ✅ AJOUTÉ: Unité (m², ml, kg, h, u, etc.)
  reference?: string;  // ✅ AJOUTÉ: Référence produit (SKU)
  lineType?: 'ITEM' | 'SECTION_TITLE' | 'COMMENT' | 'SUBTOTAL';
  groupId?: string;
  groupTitle?: string;
  discountType?: 'NONE' | 'PERCENTAGE' | 'FIXED';
  discountValue?: number;
  discountAmount?: number;
  // S10 — Tarifs dégressifs (métadonnées pour auto-apply côté frontend)
  priceTiers?: Array<{ minQuantity: number; unitPrice: number }>;
  pricingMode?: 'VOLUME' | 'GRADUATED';
  baseUnitPrice?: number;
  tierApplied?: boolean;
  // S13 — Tarif spécifique par client
  clientPriceApplied?: boolean;
  // S1 — Description type toggle (commerciale/technique/les deux)
  descriptionType?: 'COMMERCIAL' | 'TECHNICAL' | 'BOTH';
  technicalDescription?: string;
}

// Types de base partagés (camelCase ONLY)
export interface BaseDocumentEditData {
  id?: string;
  date: string; // yyyy-MM-dd
  clientId?: string;  // ✅ AJOUTÉ - ID du client (obligatoire pour création backend)

  // Client
  clientName: string;
  clientCompany: string;
  clientEmail: string;
  clientAddressLine1: string;
  clientCity: string;
  clientZipCode: string;
  clientCountry: string;
  clientPhone: string;
  clientVatNumber: string;

  // Items et totaux
  items: DocumentEditItem[];
  subtotal: number;
  tax: number;
  total: number;

  // Remise
  discountType: 'none' | 'percentage' | 'fixed';
  discountValue: number;
  discountAmount: number;

  // Notes & Titre (alignés sur Prisma)
  title?: string;
  internalNotes?: string;  // Notes internes (non imprimées)
  termsConditions?: string;  // Conditions générales (imprimées)

  // S6 — Autoliquidation TVA BTP sous-traitance
  isReverseCharge?: boolean;

  // Données de l'entreprise (snapshot)
  companyName: string;
  companyAddressLine1: string;
  companyCity: string;
  companyZipCode: string;
  companyCountry: string;
  companyPhone: string;
  companyEmail: string;
  companySiret: string;
  companyVatNumber: string;
}

// Types spécifiques
export interface InvoiceEditData extends BaseDocumentEditData {
  invoiceNumber: string;
  dueDate: string;
  purchaseOrderNumber?: string;
  reference?: string;
  projectName?: string;
  status?: 'DRAFT' | 'SENT' | 'PARTIALLY_PAID' | 'PAID' | 'OVERDUE' | 'CANCELED';
  paymentMethod: string; // OBLIGATOIRE pour Factur-X EN 16931
}

export interface QuoteEditData extends BaseDocumentEditData {
  quoteNumber: string;
  validUntil: string;

  // ✅ AJOUTÉ: Workflow signature (conforme Prisma)
  status?: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED';
  signatureStatus?: 'UNSIGNED' | 'SIGNED' | 'REFUSED';
  signedAt?: string; // Date ISO
  acceptedBy?: string;
  rejectionReason?: string;

  // ✅ AJOUTÉ: Champs métier (conforme Prisma)
  internalNotes?: string; // Notes internes (différent de notes)
  termsConditions?: string; // Conditions générales
  // title est hérité de BaseDocumentEditData

  // ✅ AJOUTÉ: Multi-devise (conforme Prisma)
  currencyCode?: string; // Devise du devis (default: EUR)
  baseCurrencyCode?: string; // Devise de base (default: EUR)
  baseTotalHT?: number; // Total HT dans la devise de base
  baseTotalTTC?: number; // Total TTC dans la devise de base
  exchangeRate?: number; // Taux de change
}

// Types d'items exportés pour compatibilité
export type InvoiceEditItem = DocumentEditItem;
export type QuoteEditItem = DocumentEditItem;

// Configuration pour chaque type de document
interface DocumentConfig<T> {
  documentType: 'invoice' | 'quote';
  api: typeof invoicesAPI | typeof quotesAPI;
  route: string;
  numberField: keyof T;
  dateEndField: keyof T;
  successMessages: {
    created: string;
    updated: string;
  };
}

/**
 * Hook générique pour gérer l'édition d'un document (facture ou devis)
 * Gère l'état local, les calculs automatiques et la sauvegarde
 *
 * @template T - Type de données du document (InvoiceEditData ou QuoteEditData)
 * @param defaultTaxRate - Taux de TVA par défaut à utiliser pour les nouveaux items
 */
export function useDocumentEdit<T extends InvoiceEditData | QuoteEditData>(
  config: DocumentConfig<T>,
  initialData: T,
  vatRegime?: string,
  onSuccess?: (documentId: string) => void,
  defaultTaxRate?: { id: string; rate: number }
) {
  const { documentType, api, route, numberField, dateEndField, successMessages } = config;

  // Normaliser les dates au format yyyy-MM-dd pour les inputs HTML
  const normalizedData = {
    ...initialData,
    date: formatDateForInput(initialData.date),
    [dateEndField]: formatDateForInput((initialData as any)[dateEndField as string] as string),
  } as T;

  const [documentData, setDocumentData] = useState<T>(normalizedData);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false); // P1-FIX: useRef pour guard anti-double-clic (pas de stale closure)
  const [hasChanges, setHasChanges] = useState(false);
  const navigate = useNavigate();

  const isExempt = isVatExempt(vatRegime);

  // P4-FIX: Effet unique pour discountAmount + totaux (évite cascade de 2 renders)
  // Preview indicatif uniquement — le backend recalcule via AmountCalculationService
  useEffect(() => {
    const calculableItems = documentData.items.filter(i => !i.lineType || i.lineType === 'ITEM');
    const itemsSubtotal = Math.round(calculableItems.reduce((sum, item) => sum + item.total, 0) * 100) / 100;

    // 1. Calculer discountAmount depuis type/value
    let discountAmount = 0;
    if (documentData.discountType === 'percentage') {
      discountAmount = Math.round((itemsSubtotal * documentData.discountValue) / 100 * 100) / 100;
    } else if (documentData.discountType === 'fixed') {
      discountAmount = documentData.discountValue;
    }

    // 2. Calculer totaux avec la remise
    const { totalVat, total, subtotal } = calculateTotalsWithVat(
      calculableItems,
      discountAmount
    );

    setDocumentData(prev => ({
      ...prev,
      discountAmount,
      subtotal,
      tax: totalVat,
      total,
    }));
  }, [documentData.items, documentData.discountType, documentData.discountValue]);

  // ✅ AJOUTÉ: Conversion automatique multi-devise pour quotes
  useEffect(() => {
    if (documentType === 'quote') {
      const quoteData = documentData as QuoteEditData;
      const exchangeRate = quoteData.exchangeRate || 1;
      const currencyCode = quoteData.currencyCode || 'EUR';
      const baseCurrencyCode = quoteData.baseCurrencyCode || 'EUR';

      // Preview indicatif uniquement — le backend recalcule via AmountCalculationService
      // Arrondi à 2 décimales pour l'affichage
      if (currencyCode !== baseCurrencyCode) {
        const baseTotalHT = Math.round(documentData.subtotal * exchangeRate * 100) / 100;
        const baseTotalTTC = Math.round(documentData.total * exchangeRate * 100) / 100;

        setDocumentData(prev => ({
          ...prev,
          baseTotalHT,
          baseTotalTTC,
        } as T));
      } else {
        // Même devise: copier les totaux
        setDocumentData(prev => ({
          ...prev,
          baseTotalHT: documentData.subtotal,
          baseTotalTTC: documentData.total,
        } as T));
      }
    }
  }, [
    documentType,
    documentData.subtotal,
    documentData.total,
    (documentData as QuoteEditData).currencyCode,
    (documentData as QuoteEditData).baseCurrencyCode,
    (documentData as QuoteEditData).exchangeRate,
  ]);

  // S1 — Hydrater technicalDescription depuis Product API au chargement
  // technicalDescription est sur le modèle Product, pas sur InvoiceItem/QuoteItem
  // On le récupère une seule fois à l'init pour que le toggle soit visible
  useEffect(() => {
    const hydrateDescriptions = async () => {
      // Collecter les productIds uniques des items qui n'ont pas encore technicalDescription
      const productIdsToFetch = new Set<string>();
      documentData.items.forEach(item => {
        if (item.productId && !item.technicalDescription) {
          productIdsToFetch.add(item.productId);
        }
      });

      if (productIdsToFetch.size === 0) return;

      // Fetch en parallèle
      const productMap = new Map<string, string>();
      const results = await Promise.allSettled(
        Array.from(productIdsToFetch).map(async (pid) => {
          const product = await productsAPI.getById(pid);
          if (product.technicalDescription) {
            productMap.set(pid, product.technicalDescription);
          }
        })
      );

      // Ignorer les erreurs individuelles (produit supprimé, etc.)

      if (productMap.size === 0) return;

      setDocumentData(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.productId && productMap.has(item.productId) && !item.technicalDescription
            ? { ...item, technicalDescription: productMap.get(item.productId)! }
            : item
        ),
      }));
    };

    hydrateDescriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only

  // Marquer comme modifié
  const markAsChanged = useCallback(() => {
    setHasChanges(true);
  }, []);

  // Mettre à jour un champ
  const updateField = useCallback((field: keyof T, value: any) => {
    setDocumentData(prev => ({
      ...prev,
      [field]: value,
    }));
    markAsChanged();
  }, [markAsChanged]);

  // Mettre à jour le type de remise (réinitialise la valeur)
  const updateDiscountType = useCallback((type: 'none' | 'percentage' | 'fixed') => {
    setDocumentData(prev => ({
      ...prev,
      discountType: type,
      discountValue: 0,
      discountAmount: 0,
    }));
    markAsChanged();
  }, [markAsChanged]);

  // Mettre à jour la valeur de remise
  const updateDiscountValue = useCallback((value: number) => {
    setDocumentData(prev => ({
      ...prev,
      discountValue: value,
    }));
    markAsChanged();
  }, [markAsChanged]);

  // Mettre à jour un item
  const updateItem = useCallback((index: number, field: keyof DocumentEditItem, value: any) => {
    setDocumentData(prev => {
      const newItems = [...prev.items];
      const item = { ...newItems[index], [field]: value };

      // S10 — Auto-apply tier pricing when quantity changes
      const isCalculable = !item.lineType || item.lineType === 'ITEM';
      if (isCalculable && field === 'quantity' && item.priceTiers && item.priceTiers.length > 0 && item.baseUnitPrice !== undefined) {
        const resolved = resolveTierPrice(
          value as number,
          item.baseUnitPrice,
          item.pricingMode || 'VOLUME',
          item.priceTiers
        );
        item.unitPrice = resolved.effectiveUnitPrice;
        item.tierApplied = resolved.tierApplied;
      }

      // Recalculer le total de l'item (uniquement pour les lignes ITEM)
      if (isCalculable) {
        const gross = item.quantity * item.unitPrice;
        let lineDiscount = 0;
        if (item.discountType === 'PERCENTAGE' && item.discountValue) {
          lineDiscount = Math.min(gross * item.discountValue / 100, gross);
        } else if (item.discountType === 'FIXED' && item.discountValue) {
          lineDiscount = Math.min(item.discountValue, gross);
        }
        item.discountAmount = Math.round(lineDiscount * 100) / 100;
        item.total = Math.round((gross - lineDiscount) * 100) / 100;
      }

      newItems[index] = item;
      return { ...prev, items: newItems };
    });
    markAsChanged();
  }, [markAsChanged]);

  // Ajouter un item
  const addItem = useCallback(() => {
    const newItem: DocumentEditItem = {
      id: crypto.randomUUID(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      // ✅ Utiliser le taux par défaut fourni, ou fallback sur 0/20
      taxPercent: isExempt ? 0 : (defaultTaxRate?.rate ?? 20),
      taxRateId: isExempt ? undefined : defaultTaxRate?.id,  // ✅ Initialiser taxRateId si fourni
      total: 0,
      position: documentData.items.length,  // ✅ AJOUTÉ: V2 utilise position
    };

    setDocumentData(prev => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
    markAsChanged();
  }, [isExempt, markAsChanged, documentData.items.length, defaultTaxRate]);

  // Supprimer un item
  const removeItem = useCallback((index: number) => {
    setDocumentData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
    markAsChanged();
  }, [markAsChanged]);

  // Ajouter un titre de section
  // C5-FIX: Calculer position DANS le callback setDocumentData pour éviter stale closure
  const addSectionTitle = useCallback(() => {
    setDocumentData(prev => ({
      ...prev,
      items: [...prev.items, {
        id: crypto.randomUUID(),
        description: '',
        quantity: 0,
        unitPrice: 0,
        taxPercent: 0,
        total: 0,
        position: prev.items.length,
        lineType: 'SECTION_TITLE' as const,
        groupId: crypto.randomUUID(),
      }],
    }));
    markAsChanged();
  }, [markAsChanged]);

  const addComment = useCallback(() => {
    setDocumentData(prev => ({
      ...prev,
      items: [...prev.items, {
        id: crypto.randomUUID(),
        description: '',
        quantity: 0,
        unitPrice: 0,
        taxPercent: 0,
        total: 0,
        position: prev.items.length,
        lineType: 'COMMENT' as const,
      }],
    }));
    markAsChanged();
  }, [markAsChanged]);

  const addSubtotal = useCallback(() => {
    setDocumentData(prev => ({
      ...prev,
      items: [...prev.items, {
        id: crypto.randomUUID(),
        description: 'Sous-total',
        quantity: 0,
        unitPrice: 0,
        taxPercent: 0,
        total: 0,
        position: prev.items.length,
        lineType: 'SUBTOTAL' as const,
      }],
    }));
    markAsChanged();
  }, [markAsChanged]);

  // Sauvegarder le document
  const saveDocument = useCallback(async () => {
    // P1-FIX: useRef guard — immunisé contre les stale closures React
    if (isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;
    setIsSaving(true);

    try {
      // ✅ CORRIGÉ: Préparer les données conformément au backend
      const apiData: Record<string, unknown> = {
        // ✅ Items formatés pour le backend (avec support lineType)
        items: documentData.items.map((item, index) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxPercent: item.taxPercent,
          taxRateId: item.taxRateId,
          productId: item.productId,
          position: index + 1,  // Position commence à 1
          lineType: item.lineType || 'ITEM',
          groupId: item.groupId,
          groupTitle: item.groupTitle,
          unit: item.unit,
          reference: item.reference,
          discountType: item.discountType || 'NONE',
          discountValue: item.discountValue || 0,
          descriptionType: item.descriptionType || 'COMMERCIAL',
        })),

        // ✅ Remise en UPPERCASE
        discountType: documentData.discountType.toUpperCase() as "NONE" | "PERCENTAGE" | "FIXED",
        discountValue: documentData.discountValue,

        // Notes & Titre (alignés sur Prisma)
        // Utiliser null (pas undefined) pour que Prisma efface le champ en BDD
        title: documentData.title || null,
        internalNotes: documentData.internalNotes || null,
        termsConditions: documentData.termsConditions || null,

        // S6 — Autoliquidation TVA BTP sous-traitance
        isReverseCharge: documentData.isReverseCharge || false,
      };

      // Ajouter les champs spécifiques selon le type de document
      if (documentType === 'invoice') {
        const invoiceData = documentData as InvoiceEditData;

        // ✅ CORRIGÉ: clientId obligatoire pour création de facture
        if (!documentData.clientId) {
          throw new Error('Veuillez sélectionner un client pour créer une facture');
        }
        apiData.clientId = documentData.clientId;

        if (invoiceData.invoiceNumber) {
          apiData.invoiceNumber = invoiceData.invoiceNumber;
        }
        apiData.issueDate = convertToISO(invoiceData.date);
        apiData.dueDate = convertToISO(invoiceData.dueDate);
        apiData.purchaseOrderNumber = invoiceData.purchaseOrderNumber || '';
        apiData.reference = invoiceData.reference || '';
        apiData.projectName = invoiceData.projectName || '';
        apiData.status = invoiceData.status || 'DRAFT';
        apiData.paymentMethod = invoiceData.paymentMethod || '';
      } else {
        const quoteData = documentData as QuoteEditData;

        // ✅ CORRIGÉ: clientId obligatoire pour création
        if (!documentData.clientId) {
          throw new Error('clientId est obligatoire pour créer un devis');
        }
        apiData.clientId = documentData.clientId;

        // ✅ CORRIGÉ: issueDate et validUntil
        apiData.issueDate = convertToISO(quoteData.date);
        apiData.validUntil = convertToISO(quoteData.validUntil);

        if (quoteData.quoteNumber) {
          apiData.quoteNumber = quoteData.quoteNumber;
        }

        // ✅ Workflow signature
        if (quoteData.status) apiData.status = quoteData.status;
        if (quoteData.signatureStatus) apiData.signatureStatus = quoteData.signatureStatus;
        if (quoteData.signedAt) apiData.signedAt = quoteData.signedAt;
        if (quoteData.acceptedBy) apiData.acceptedBy = quoteData.acceptedBy;
        if (quoteData.rejectionReason) apiData.rejectionReason = quoteData.rejectionReason;

        // ✅ Champs métier (title, internalNotes, termsConditions sont envoyés dans le bloc commun)

        // ✅ Multi-devise
        if (quoteData.currencyCode) apiData.currencyCode = quoteData.currencyCode;
        if (quoteData.baseCurrencyCode) apiData.baseCurrencyCode = quoteData.baseCurrencyCode;
        if (quoteData.baseTotalHT !== undefined) apiData.baseTotalHT = quoteData.baseTotalHT;
        if (quoteData.baseTotalTTC !== undefined) apiData.baseTotalTTC = quoteData.baseTotalTTC;
        if (quoteData.exchangeRate !== undefined) apiData.exchangeRate = quoteData.exchangeRate;
      }

      let savedDocumentId = documentData.id;

      if (documentData.id) {
        // Mise à jour
        await api.update(documentData.id, apiData);
        notify.success(successMessages.updated, 'Sauvegarde réussie');
      } else {
        // Création
        const created = await api.create(apiData);
        savedDocumentId = created.id;
        notify.success(successMessages.created, 'Création réussie');
      }

      setHasChanges(false);

      // Appeler le callback avant navigation (pour génération PDF en arrière-plan)
      if (onSuccess && savedDocumentId) {
        onSuccess(savedDocumentId);
      }

      navigate(route);
    } catch (error: any) {
      notify.error('Erreur : ' + error.message, 'Erreur de sauvegarde');
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [documentData, navigate, api, route, numberField, dateEndField, successMessages, onSuccess, documentType]);

  // S7 — Ajouter les lignes d'un ouvrage composé (explosion)
  const addCompositeLines = useCallback(async (productId: string) => {
    try {
      const lines = await productsAPI.explodeToLines(productId);
      if (!lines || lines.length === 0) return;

      // Calculer position dans le setter fonctionnel pour éviter stale closure
      setDocumentData(prev => {
        const newItems: DocumentEditItem[] = lines.map((line: any, idx: number) => ({
          id: crypto.randomUUID(),
          description: line.description || '',
          quantity: line.quantity ?? 0,
          unitPrice: line.unitPrice ?? 0,
          taxPercent: line.taxPercent ?? (isExempt ? 0 : (defaultTaxRate?.rate ?? 20)),
          taxRateId: line.taxRateId || defaultTaxRate?.id,
          total: (line.quantity ?? 0) * (line.unitPrice ?? 0),
          position: prev.items.length + idx,
          lineType: line.lineType as DocumentEditItem['lineType'],
          groupId: line.groupId,
          groupTitle: line.groupTitle,
          productId: line.productId,
          unit: line.unit || '',
          reference: '',
          descriptionType: 'COMMERCIAL' as const,
          technicalDescription: line.technicalDescription || '',
        }));

        return {
          ...prev,
          items: [...prev.items, ...newItems],
        };
      });
      markAsChanged();
    } catch (error: any) {
      notify.error(error.message || "Erreur lors de l'ajout de l'ouvrage");
    }
  }, [isExempt, markAsChanged, defaultTaxRate]);

  // S10/S13 — Ajouter un produit depuis le catalogue avec tarifs dégressifs + tarifs clients
  const addProductFromCatalog = useCallback(async (product: any) => {
    // S7 — Si ouvrage composé, exploser en lignes groupées
    if (product.isComposite) {
      await addCompositeLines(product.id);
      return;
    }

    // S13 — Vérifier le tarif spécifique par client (priorité 1)
    let clientPrice: number | null = null;
    if (product.id && documentData.clientId) {
      try {
        const priceMap = await productsAPI.resolveClientPrices(
          documentData.clientId, [product.id]
        );
        if (priceMap[product.id] !== undefined) {
          clientPrice = priceMap[product.id];
        }
      } catch {
        // Silently ignore — client may not have specific pricing
      }
    }

    // S10 — Pré-charger les paliers de prix (priorité 2, skip si prix client trouvé)
    let tierMeta: Pick<DocumentEditItem, 'priceTiers' | 'pricingMode' | 'baseUnitPrice'> = {};
    if (product.id && clientPrice === null) {
      try {
        const tierData = await productsAPI.getTiers(product.id);
        if (tierData.tiers.length > 0) {
          tierMeta = {
            priceTiers: tierData.tiers.map((t: any) => ({
              minQuantity: Number(t.minQuantity),
              unitPrice: Number(t.unitPrice),
            })),
            pricingMode: tierData.pricingMode,
            baseUnitPrice: tierData.baseUnitPrice || 0,
          };
        }
      } catch {
        // Silently ignore — product may not have tiers
      }
    }

    // S13 utilise le prix client, sinon prix catalogue
    const unitPrice = clientPrice !== null ? clientPrice : (Number(product.unitPrice) || 0);
    const taxPercent = isExempt ? 0 : (product.vatRate ?? (defaultTaxRate?.rate ?? 20));

    // Un seul setState atomique — évite les race conditions avec React batching
    setDocumentData(prev => {
      const newItem: DocumentEditItem = {
        id: crypto.randomUUID(),
        description: product.name + (product.description ? ` - ${product.description}` : ''),
        quantity: 1,
        unitPrice,
        taxPercent,
        taxRateId: isExempt ? undefined : defaultTaxRate?.id,
        total: unitPrice, // qty=1 × unitPrice
        position: prev.items.length,
        productId: product.id,
        unit: product.unit || '',
        reference: product.sku || '',
        technicalDescription: product.technicalDescription || '',
        descriptionType: 'COMMERCIAL' as const,
        ...(clientPrice !== null ? { clientPriceApplied: true } : tierMeta),
      };
      return { ...prev, items: [...prev.items, newItem] };
    });
    markAsChanged();
  }, [isExempt, markAsChanged, addCompositeLines, defaultTaxRate, documentData.clientId]);

  return {
    documentData,
    isSaving,
    hasChanges,
    updateField,
    updateDiscountType,
    updateDiscountValue,
    updateItem,
    addItem,
    removeItem,
    addSectionTitle,
    addComment,
    addSubtotal,
    addCompositeLines,
    addProductFromCatalog,
    saveDocument,
  };
}
